import { Components, Theme } from '@mui/material/styles';
import '@mui/x-chat/themeAugmentation';
import { ChatRowMarine, themeColor } from './color';
import { typography } from './typography';
import { radius } from '../radius';

// Register the prototype-only `xsmall` input size (used for dense desktop
// surfaces like the CAM filters) with MUI's size unions.
declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    xsmall: true;
  }
}
declare module '@mui/material/InputBase' {
  interface InputBasePropsSizeOverrides {
    xsmall: true;
  }
}
declare module '@mui/material/FormControl' {
  interface FormControlPropsSizeOverrides {
    xsmall: true;
  }
}
declare module '@mui/material/ToggleButtonGroup' {
  interface ToggleButtonGroupPropsSizeOverrides {
    xsmall: true;
  }
}
declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsSizeOverrides {
    xsmall: true;
  }
}

// IconButton has no `variant` of its own; `chatControl` is the filled coral
// disc that sends a message or stops a recording in a chat.
declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    variant?: 'chatControl';
  }
}

// Card's `variant` prop is inherited from Paper, so the custom variant is
// registered on Paper's override interface (it's only styled on Card below).
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    chatReply: true;
    chatReplyQuiet: true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    padded: true;
    featured: true;
    option: true;
    optionSelected: true;
    optionFillSelected: true;
    chatOffer: true;
    chatGo: true;
    chatAttached: true;
    chatBubbleFilled: true;
    chatBubbleFailed: true;
    chatBubbleOutlined: true;
  }
}

// The option-card look shared by RadioGroup's card variants: a 16px inset,
// clickable, with a smooth border/fill transition on selection.
const optionCard = {
  padding: 16,
  cursor: 'pointer',
  transition: 'border-color 150ms ease, background-color 150ms ease',
};

const chatBubblePadding = '10px 16px';

// A tappable row in a chat thread, rendered as a button, inset like a bubble.
const chatRow = {
  ...optionCard,
  padding: chatBubblePadding,
  width: '100%',
  textAlign: 'left' as const,
  boxShadow: 'none',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: radius.md,
  transition:
    'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
  '&:active': { transform: 'scale(0.99)' },
  '&:disabled': { opacity: 0.6, transform: 'none' },
};

// Shared by MUI X Chat's own text bubbles and the Paper variants used for
// bubbles it doesn't render, so the two always match.
const chatBubble = {
  borderRadius: radius.lg,
  padding: chatBubblePadding,
};

const chatBubbleFilled = {
  ...chatBubble,
  backgroundColor: themeColor.palette.custom.coral[400],
  color: themeColor.palette.custom.neutral[50],
  border: 'none',
  borderBottomRightRadius: radius.sm,
};

const chatBubbleFailed = {
  ...chatBubbleFilled,
  backgroundColor: themeColor.palette.error.main,
};

const chatBubbleOutlined = {
  ...chatBubble,
  backgroundColor: themeColor.palette.custom.neutral[50],
  border: `1px solid ${themeColor.palette.custom.neutral[200]}`,
  borderBottomLeftRadius: radius.sm,
};

/** Widest a bubble grows, as a share of its row. */
const CHAT_BUBBLE_MAX_WIDTH = '82%';

/** Marks a message bubble whose content draws its own, e.g. an attachment. */
export const BARE_BUBBLE_CLASS = 'MuiChatMessage-bubbleBare';

/** Marks a message bubble that runs the full width of the thread, e.g. a card. */
export const FULL_WIDTH_BUBBLE_CLASS = 'MuiChatMessage-bubbleFull';

/** Marks a thread whose replies are charcoal on gardenia, as the 2.0 design has them. */
export const GARDENIA_REPLIES_CLASS = 'MuiChatBox-gardeniaReplies';

export const components: Components<Omit<Theme, 'components'>> = {
  MuiSnackbar: {
    defaultProps: {
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    },
    styleOverrides: {
      root: {
        maxWidth: 400,
      },
    },
  },
  MuiBottomNavigation: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.custom.limeGreen[200],
        gap: theme.spacing(1),
      }),
    },
  },
  MuiBottomNavigationAction: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.custom.limeGreen[700],
        borderRadius: 9999,
        '&.Mui-selected': {
          backgroundColor: theme.palette.custom.limeGreen[500],
          color: theme.palette.custom.marineGreen[700],
        },
      }),
    },
  },
  MuiAvatar: {
    styleOverrides: {
      // Default (no `color` prop): lime fill, marine initials.
      root: ({ theme }) => ({
        backgroundColor: theme.palette.custom.limeGreen[500],
        color: theme.palette.custom.marineGreen[700],
      }),
    },
    // Colour variants — opt in with `color='primary' | 'secondary' | brand`.
    // Each pairs the palette fill with its own contrastText so initials stay
    // legible (otherwise a dark `bgcolor` keeps the default dark text).
    variants: (
      [
        'primary',
        'secondary',
        'cherry',
        'warmOrange',
        'limeGreen',
        'lavender',
        'sunflower',
      ] as const
    ).map((color) => ({
      props: { color },
      style: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette[color].main,
        color: theme.palette[color].contrastText,
      }),
    })),
  },
  MuiContainer: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: radius.sm,
      },
    },
  },
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        // MUI v9 changed minWidth from hardcoded 56px to theme.spacing(4.5).
        // Our custom spacing function resolves fractional factors as factor*4
        // instead of MUI's default factor*8, yielding 18px instead of 36px.
        minWidth: 36,
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundColor: themeColor.palette.custom.neutral[50],
        border: `1px solid ${themeColor.palette.custom.neutral[200]}`,
        borderRadius: radius.md,
      },
    },
    variants: [
      {
        // `padded` gives a uniform 16px inset (the design's standard spacing
        // token) for content placed directly in the Card — as opposed to
        // CardContent's heavier 24px bottom, which suits richer content cards.
        props: { variant: 'padded' },
        style: { padding: 16 },
      },
      {
        // `featured` is a padded card with lavender tint — the promoted /
        // primary choice in a group (e.g. "Apply for power of attorney").
        props: { variant: 'featured' },
        style: {
          padding: 16,
          backgroundColor: themeColor.palette.custom.lavender[50],
          borderColor: themeColor.palette.custom.lavender[200],
        },
      },
      {
        // `listItem` is a clickable card row with press feedback — used for
        // navigable lists (calls, documents, etc.). Pair with CardHeader.
        props: { variant: 'listItem' },
        style: {
          cursor: 'pointer',
          transition: 'transform 120ms ease',
          '&:active': { transform: 'scale(0.98)' },
        },
      },
      {
        // `option` is an unselected radio option card.
        props: { variant: 'option' },
        style: optionCard,
      },
      {
        // `optionSelected` is a selected radio option card — Lime Green fill
        // with a Marine Green border.
        props: { variant: 'optionSelected' },
        style: {
          ...optionCard,
          borderColor: themeColor.palette.primary.main,
          backgroundColor: themeColor.palette.custom.limeGreen[200],
        },
      },
      {
        // `optionFillSelected` is the box-fill selected option card — Coral fill
        // with a Marine Green border (onboarding).
        props: { variant: 'optionFillSelected' },
        style: {
          ...optionCard,
          borderColor: themeColor.palette.primary.main,
          backgroundColor: themeColor.palette.custom.coral[100],
        },
      },
      {
        // `chatOffer` is a thing on offer in a chat: a marine tint that deepens
        // on hover.
        props: { variant: 'chatOffer' },
        style: {
          ...chatRow,
          backgroundColor: ChatRowMarine[50],
          borderColor: ChatRowMarine[100],
          '&:hover': {
            backgroundColor: ChatRowMarine[100],
            borderColor: ChatRowMarine[200],
          },
          '&:active': {
            ...chatRow['&:active'],
            backgroundColor: ChatRowMarine[100],
            borderColor: themeColor.palette.primary.main,
          },
        },
      },
      {
        // `chatGo` is the way on: the selected lime surface with a marine border.
        props: { variant: 'chatGo' },
        style: {
          ...chatRow,
          backgroundColor: themeColor.palette.custom.limeGreen[200],
          borderColor: themeColor.palette.primary.main,
          '&:hover': { borderColor: themeColor.palette.primary.dark },
        },
      },
      {
        // `chatAttached` is an answer joined to the question above it, the way
        // a messaging app attaches buttons: a plain surface that greens on hover.
        props: { variant: 'chatAttached' },
        style: {
          ...chatRow,
          backgroundColor: themeColor.palette.background.paper,
          borderColor: themeColor.palette.custom.neutral[200],
          '&:hover': {
            borderColor: themeColor.palette.custom.marineGreen[300],
          },
        },
      },
    ],
  },
  MuiChip: {
    variants: [
      {
        // `chatReply` is the likely answer, offered over the text field in the
        // chat rows' marine.
        props: { variant: 'chatReply' },
        style: {
          color: themeColor.palette.text.primary,
          backgroundColor: ChatRowMarine[50],
          '&.MuiChip-clickable:hover': { backgroundColor: ChatRowMarine[100] },
        },
      },
      {
        // `chatReplyQuiet` is any other answer, a step quieter.
        props: { variant: 'chatReplyQuiet' },
        style: {
          color: themeColor.palette.text.primary,
          backgroundColor: themeColor.palette.custom.neutral[200],
          '&.MuiChip-clickable:hover': {
            backgroundColor: themeColor.palette.custom.neutral[300],
          },
        },
      },
    ],
  },
  MuiIconButton: {
    variants: [
      {
        props: { variant: 'chatControl' },
        style: {
          width: 40,
          height: 40,
          flexShrink: 0,
          backgroundColor: themeColor.palette.custom.coral[600],
          color: themeColor.palette.common.white,
          '&:hover': {
            backgroundColor: themeColor.palette.custom.coral[700],
          },
          '&.Mui-disabled': {
            backgroundColor: themeColor.palette.custom.neutral[200],
            color: themeColor.palette.common.white,
          },
        },
      },
    ],
  },
  // Bottom-sheet dialog: opt in with `className='bottom-sheet'`. On mobile the
  // dialog docks to the bottom as a full-width, rounded-top sheet; on desktop it
  // stays a centred dialog. `bottom-sheet--above-modal` lifts it over another
  // open modal (e.g. a confirm sheet over a drawer). See atoms/BottomSheetDialog.
  MuiDialog: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.bottom-sheet--warm .MuiDialog-paper': {
          backgroundColor: theme.palette.background.default,
        },
        '&.bottom-sheet--above-modal': {
          zIndex: theme.zIndex.modal + 1,
        },
        [theme.breakpoints.down('sm')]: {
          '&.bottom-sheet': {
            '& .MuiDialog-container': { alignItems: 'flex-end' },
            '& .MuiDialog-paper': {
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              borderRadius: `${radius.lg}px ${radius.lg}px 0 0`,
              maxHeight: `calc(100dvh - ${theme.spacing(5)})`,
              overflow: 'hidden',
            },
          },
        },
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.h2,
      }),
    },
  },
  MuiCardHeader: {
    defaultProps: {
      slotProps: {
        title: { variant: 'h2' },
        subheader: { variant: 'subtitle1' },
      },
    },
    styleOverrides: {
      root: {
        // `list-item` class: compact padding with centered action for Card
        // variant='listItem'. Apply className='list-item' to CardHeader.
        '&.list-item': {
          padding: 20,
          '& .MuiCardHeader-action': {
            marginLeft: 16,
            marginRight: 0,
            alignSelf: 'center',
          },
        },
      },
      action: {
        alignSelf: 'center',
        marginTop: 0,
        marginBottom: 0,
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    variants: [
      {
        props: { variant: 'chatBubbleFilled' },
        style: chatBubbleFilled,
      },
      {
        props: { variant: 'chatBubbleFailed' },
        style: chatBubbleFailed,
      },
      {
        props: { variant: 'chatBubbleOutlined' },
        style: chatBubbleOutlined,
      },
    ],
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: radius.xxl,
        // Dashed outline (e.g. the "Upload document" deep-link CTA).
        '&.dashed': { borderStyle: 'dashed' },
      },
      sizeLarge: {
        minHeight: 56,
        fontSize: '1.25rem',
      },
      sizeMedium: {
        minHeight: 44,
        fontSize: '1rem',
      },
      sizeSmall: {
        fontSize: '1rem',
      },
    },
  },
  // Segmented control (pill filter). Opt in with `className='segmented'` —
  // e.g. the member actions All / To do / Done filter (ActionStatusTabs).
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.segmented': {
          minHeight: 0,
          width: '100%',
          borderRadius: radius.xxl,
          backgroundColor: theme.palette.custom.sunflower[400],
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            flex: 1,
            maxWidth: 'none',
            minWidth: 0,
            minHeight: 0,
            // 9px vertical / 16px horizontal per the segmented-control spec
            padding: `9px ${theme.spacing(3)}`,
            borderRadius: radius.xxl,
            textTransform: 'none',
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: '1rem',
            letterSpacing: '0.4px',
            color: theme.palette.custom.warmOrange[900],
            '&.Mui-selected': {
              color: theme.palette.custom.warmOrange[900],
              backgroundColor: theme.palette.custom.warmOrange[400],
            },
          },
        },
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: '14px',
        marginBottom: 4,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: '0.75rem',
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      shrink: true,
    },
    styleOverrides: {
      root: {
        ...typography.inputLabel,
        '&.MuiInputLabel-filled': {
          transform: 'translate(12px, 9px) scale(1)',
        },
        '&.MuiInputLabel-filled.MuiInputLabel-sizeSmall': {
          transform: 'translate(12px, 6px) scale(1)',
        },
        '&.MuiInputLabel-outlined': {
          transform: 'translate(14px, -8px) scale(1)',
          backgroundColor: themeColor.palette.background.default,
          paddingLeft: 4,
          paddingRight: 4,
        },
        '&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall': {
          transform: 'translate(14px, -8px) scale(1)',
        },
        '&.MuiInputLabel-standard': {
          transform: 'translate(0, -1.5px) scale(1)',
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'filled',
    },
  },
  MuiFormControl: {
    defaultProps: {
      variant: 'filled',
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: themeColor.palette.custom.neutral[50],
        border: `1px solid ${themeColor.palette.custom.neutral[500]}`,
        borderRadius: 4,
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
        '&:hover': {
          backgroundColor: themeColor.palette.custom.neutral[50],
        },
        '&.Mui-focused': {
          backgroundColor: themeColor.palette.custom.neutral[50],
          borderColor: themeColor.palette.primary.main,
          boxShadow: `inset 0 0 0 1.5px ${themeColor.palette.primary.main}`,
        },
        '&:before, &:after': {
          display: 'none',
        },
        '& .MuiInputAdornment-positionStart': {
          marginTop: '0 !important',
        },
      },
      input: {
        ...typography.inputValue,
        // Default (no internal label): tight padding per Figma compact spec
        padding: '9px 12px 8px',
        '.MuiInputBase-sizeSmall &': {
          padding: '7px 10px 6px',
        },
        // With internal label: 9 (top inset) + 18 (label at lh 1.2) + 4 (gap) = 31px top
        '.MuiFormControl-root:has(> .MuiInputLabel-root) &': {
          padding: '31px 12px 8px',
        },
        // Small + internal label: 6 (top inset) + 18 (label) + 4 (gap) = 28px top
        '.MuiFormControl-root:has(> .MuiInputLabel-sizeSmall) &': {
          padding: '28px 10px 4px',
        },
      },
    },
    variants: [
      {
        // size='xsmall': a notch below llama's small. The root height
        // is pinned so text inputs and selects come out identical
        // regardless of their internal markup.
        props: { size: 'xsmall' },
        style: {
          fontSize: 13,
          height: 30,
          '& .MuiInputBase-input': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 8,
            paddingRight: 8,
            fontSize: 13,
            height: '100%',
            boxSizing: 'border-box',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
          },
        },
      },
    ],
  },
  MuiRadioGroup: {
    styleOverrides: {
      root: {
        gap: 8,
      },
    },
  },
  MuiToggleButtonGroup: {
    variants: [
      {
        // size='xsmall': a notch below llama's small. The root height
        // is pinned so text inputs and selects come out identical
        // regardless of their internal markup.
        props: { size: 'xsmall' },
        style: {
          fontSize: 13,
          height: 30,
          '& .MuiInputBase-input': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 8,
            paddingRight: 8,
            fontSize: 13,
            height: '100%',
            boxSizing: 'border-box',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
          },
        },
      },
    ],
  },
  MuiToggleButton: {
    variants: [
      {
        // size='xsmall': a notch below llama's small. The root height
        // is pinned so text inputs and selects come out identical
        // regardless of their internal markup.
        props: { size: 'xsmall' },
        style: {
          fontSize: 13,
          height: 30,
          '& .MuiInputBase-input': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 8,
            paddingRight: 8,
            fontSize: 13,
            height: '100%',
            boxSizing: 'border-box',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
          },
        },
      },
    ],
  },
  MuiSelect: {
    variants: [
      {
        // size='xsmall': a notch below llama's small. The root height
        // is pinned so text inputs and selects come out identical
        // regardless of their internal markup.
        props: { size: 'xsmall' },
        style: {
          fontSize: 13,
          height: 30,
          '& .MuiInputBase-input': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 8,
            paddingRight: 8,
            fontSize: 13,
            height: '100%',
            boxSizing: 'border-box',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
          },
        },
      },
    ],
  },
  MuiFormGroup: {
    styleOverrides: {
      root: {
        gap: 8,
      },
    },
  },

  MuiChatMessage: {
    styleOverrides: {
      root: ({ theme }) => ({
        // One gap between messages, whoever sent them.
        paddingBlock: `0 ${theme.spacing(2)}`,
        paddingInline: theme.spacing(3),
        '&.MuiChatMessage-noAvatar': { paddingInline: theme.spacing(3) },
        '&.MuiChatMessage-error .MuiChatMessage-bubble': chatBubbleFailed,
        // Outranks the role styles, which share the bubble's specificity.
        [`& .MuiChatMessage-bubble.${BARE_BUBBLE_CLASS}`]: {
          background: 'none',
          border: 'none',
          padding: 0,
        },
        [`& .MuiChatMessage-bubble.${FULL_WIDTH_BUBBLE_CLASS}`]: {
          width: '100%',
          maxWidth: 'none',
        },
      }),
      group: { marginBlockStart: 0 },
      bubble: ({ theme }) => ({
        ...chatBubble,
        ...theme.typography.body1,
        width: 'fit-content',
        maxWidth: CHAT_BUBBLE_MAX_WIDTH,
        whiteSpace: 'pre-line',
      }),
      roleAssistant: {
        '& .MuiChatMessage-bubble': {
          ...chatBubbleOutlined,
          alignSelf: 'flex-start',
        },
      },
      roleUser: {
        '& .MuiChatMessage-bubble': {
          ...chatBubbleFilled,
          alignSelf: 'flex-end',
        },
      },
      noAvatar: {
        '& .MuiChatMessage-group': {
          paddingLeft: 0,
        },
      },
    },
  },

  MuiChatBox: {
    styleOverrides: {
      root: {
        border: 'none',
        // Their text, voice notes and files alike; a failed send stays red.
        [`&.${GARDENIA_REPLIES_CLASS} .MuiChatMessage-roleUser:not(.MuiChatMessage-error) .MuiChatMessage-bubble, &.${GARDENIA_REPLIES_CLASS} .MuiPaper-chatBubbleFilled`]:
          {
            backgroundColor: themeColor.palette.custom.gardeniaDark,
            color: themeColor.palette.text.primary,
          },
      },
    },
  },

  MuiChatComposer: {
    styleOverrides: {
      root: {
        borderRadius: radius.xxl,
      },
    },
  },
};
